import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SlidersHorizontal } from "lucide-react";
import { api } from "@/lib/api";

export default function FilterSheet({ filters, setFilters, categories, defaultOpen = false, onOpenChange }) {
  const [open, setOpen] = useState(defaultOpen);
  const [cities, setCities] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [subs, setSubs] = useState([]);
  const [local, setLocal] = useState(filters);

  useEffect(() => { setLocal(filters); }, [filters, open]);
  useEffect(() => { if (defaultOpen) setOpen(true); }, [defaultOpen]);

  const handleOpenChange = (v) => {
    setOpen(v);
    if (onOpenChange) onOpenChange(v);
  };

  useEffect(() => {
    api.get("/meta/cities").then(r => setCities(r.data || [])).catch(() => {});
    api.get("/meta/markets").then(r => setMarkets(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!local.category) { setSubs([]); return; }
    api.get("/meta/subcategories", { params: { parent: local.category } }).then(r => setSubs(r.data || [])).catch(() => setSubs([]));
  }, [local.category]);

  const apply = () => { setFilters(local); setOpen(false); };
  const reset = () => { setLocal({}); setFilters({}); setOpen(false); };

  const count = Object.values(filters || {}).filter(v => v !== undefined && v !== "" && v !== false).length;

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        <button
          type="button"
          data-testid="open-filters-btn"
          className="h-11 px-3 rounded-xl border border-[#E8E6E1] bg-white flex items-center gap-2 text-sm font-semibold text-[#1A1918] hover:border-[#E65100] transition relative"
        >
          <SlidersHorizontal className="h-4 w-4" />
          Filtres
          {count > 0 && <span className="ml-1 h-5 min-w-5 px-1 rounded-full bg-[#E65100] text-white text-[10px] font-bold flex items-center justify-center">{count}</span>}
        </button>
      </SheetTrigger>
      <SheetContent side="bottom" className="h-[88vh] rounded-t-3xl bg-white overflow-y-auto">
        <SheetHeader className="text-left">
          <SheetTitle className="font-[Manrope] text-xl">Filtres</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-5 pb-32">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-[#73706B]">Catégorie</Label>
            <Select value={local.category || "all"} onValueChange={(v) => setLocal(s => ({ ...s, category: v === "all" ? undefined : v, subcategory: undefined }))}>
              <SelectTrigger className="h-12 mt-1 rounded-xl" data-testid="filter-category"><SelectValue placeholder="Toutes" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                {categories.map(c => <SelectItem key={c.slug} value={c.slug}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {subs.length > 0 && (
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#73706B]">Sous-catégorie</Label>
              <Select value={local.subcategory || "all"} onValueChange={(v) => setLocal(s => ({ ...s, subcategory: v === "all" ? undefined : v }))}>
                <SelectTrigger className="h-12 mt-1 rounded-xl" data-testid="filter-subcategory"><SelectValue placeholder="Toutes" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  {subs.map(s => <SelectItem key={s.slug} value={s.slug}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-[#73706B]">Ville</Label>
            <Select value={local.city || "all"} onValueChange={(v) => setLocal(s => ({ ...s, city: v === "all" ? undefined : v }))}>
              <SelectTrigger className="h-12 mt-1 rounded-xl" data-testid="filter-city"><SelectValue placeholder="Toutes" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">Toutes</SelectItem>
                {cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-[#73706B]">Marché</Label>
            <Select value={local.market || "all"} onValueChange={(v) => setLocal(s => ({ ...s, market: v === "all" ? undefined : v }))}>
              <SelectTrigger className="h-12 mt-1 rounded-xl" data-testid="filter-market"><SelectValue placeholder="Tous" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                {markets.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-[#73706B]">Prix (FCFA)</Label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              <Input data-testid="filter-min-price" inputMode="numeric" className="h-12 rounded-xl" placeholder="Min" value={local.min_price || ""} onChange={(e) => setLocal(s => ({ ...s, min_price: e.target.value ? parseInt(e.target.value) : undefined }))} />
              <Input data-testid="filter-max-price" inputMode="numeric" className="h-12 rounded-xl" placeholder="Max" value={local.max_price || ""} onChange={(e) => setLocal(s => ({ ...s, max_price: e.target.value ? parseInt(e.target.value) : undefined }))} />
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-[#73706B]">État</Label>
            <Select value={local.condition || "all"} onValueChange={(v) => setLocal(s => ({ ...s, condition: v === "all" ? undefined : v }))}>
              <SelectTrigger className="h-12 mt-1 rounded-xl" data-testid="filter-condition"><SelectValue placeholder="Tous" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="neuf">Neuf</SelectItem>
                <SelectItem value="occasion">Occasion</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between py-1">
            <Label htmlFor="wholesale-toggle" className="text-sm font-semibold">Vente en gros uniquement</Label>
            <Switch id="wholesale-toggle" data-testid="filter-wholesale" checked={!!local.wholesale} onCheckedChange={(v) => setLocal(s => ({ ...s, wholesale: v || undefined }))} />
          </div>
          <div className="flex items-center justify-between py-1">
            <Label htmlFor="video-toggle" className="text-sm font-semibold">Avec vidéo</Label>
            <Switch id="video-toggle" data-testid="filter-has-video" checked={!!local.has_video} onCheckedChange={(v) => setLocal(s => ({ ...s, has_video: v || undefined }))} />
          </div>
          <div className="flex items-center justify-between py-1">
            <Label htmlFor="verified-toggle" className="text-sm font-semibold">Boutiques vérifiées</Label>
            <Switch id="verified-toggle" data-testid="filter-verified" checked={!!local.verified_only} onCheckedChange={(v) => setLocal(s => ({ ...s, verified_only: v || undefined }))} />
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-[#73706B]">Trier par</Label>
            <Select value={local.sort || "score"} onValueChange={(v) => setLocal(s => ({ ...s, sort: v }))}>
              <SelectTrigger className="h-12 mt-1 rounded-xl" data-testid="filter-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="score">Pertinence</SelectItem>
                <SelectItem value="recent">Plus récents</SelectItem>
                <SelectItem value="price_asc">Prix croissant</SelectItem>
                <SelectItem value="price_desc">Prix décroissant</SelectItem>
                <SelectItem value="popular">Populaires</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-[#E8E6E1] flex gap-2">
          <Button variant="outline" className="flex-1 h-12 rounded-xl" onClick={reset} data-testid="filter-reset-btn">Réinitialiser</Button>
          <Button className="flex-1 h-12 rounded-xl bg-[#E65100] hover:bg-[#C44100]" onClick={apply} data-testid="filter-apply-btn">Appliquer</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
